import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authFirebase } from '../firebase.config';
import {
  UserPermissions,
  PermissionModule,
  PermissionAction,
  PermissionLevelName,
  PermissionCheckResult,
} from './types';
import {
  hasPermission,
  canView,
  canEdit,
  canManage,
  isViewer,
  isEditor,
  isManager,
  isAdmin,
  getModulePermission,
  extractPermissionsFromJWT,
  getAccessibleModules,
} from './utils';

interface PermissionContextValue {
  // Permission state
  permissions: UserPermissions | null;
  isSuperAdmin: boolean;
  isLoading: boolean;
  error: string | null;

  // Permission check functions
  hasPermission: (module: PermissionModule, action: PermissionAction) => PermissionCheckResult;
  canView: (module: PermissionModule) => boolean;
  canEdit: (module: PermissionModule) => boolean;
  canManage: (module: PermissionModule) => boolean;
  isViewer: (module: PermissionModule) => boolean;
  isEditor: (module: PermissionModule) => boolean;
  isManager: (module: PermissionModule) => boolean;
  isAdmin: (module: PermissionModule) => boolean; // Deprecated: Use isManager instead
  getModuleLevel: (module: PermissionModule) => PermissionLevelName;
  getAccessibleModules: () => PermissionModule[];

  // Refresh permissions
  refreshPermissions: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextValue | null>(null);

interface PermissionProviderProps {
  children: React.ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadPermissions = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const user = authFirebase.currentUser;
      if (!user) {
        setPermissions(null);
        setIsSuperAdmin(false);
        setIsLoading(false);
        return;
      }

      // Get the ID token which contains the custom claims
      const idToken = await user.getIdToken();
      const result = extractPermissionsFromJWT(idToken);

      if (result) {
        setPermissions(result.permissions);
        setIsSuperAdmin(result.isSuperAdmin);

        // Also store in localStorage for quick access
        localStorage.setItem('user_permissions', JSON.stringify(result.permissions));
        localStorage.setItem('is_superadmin', JSON.stringify(result.isSuperAdmin));
      } else {
        // Fallback to localStorage if JWT parsing fails
        const storedPermissions = localStorage.getItem('user_permissions');
        const storedSuperAdmin = localStorage.getItem('is_superadmin');

        if (storedPermissions) {
          setPermissions(JSON.parse(storedPermissions));
        }
        if (storedSuperAdmin) {
          setIsSuperAdmin(JSON.parse(storedSuperAdmin));
        }
      }
    } catch (err) {
      console.error('Error loading permissions:', err);
      setError('Failed to load permissions');

      // Try to use cached permissions
      const storedPermissions = localStorage.getItem('user_permissions');
      const storedSuperAdmin = localStorage.getItem('is_superadmin');

      if (storedPermissions) {
        setPermissions(JSON.parse(storedPermissions));
      }
      if (storedSuperAdmin) {
        setIsSuperAdmin(JSON.parse(storedSuperAdmin));
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load permissions on mount and when auth state changes
  useEffect(() => {
    const unsubscribe = authFirebase.onAuthStateChanged((user) => {
      if (user) {
        loadPermissions();
      } else {
        setPermissions(null);
        setIsSuperAdmin(false);
        setIsLoading(false);
        localStorage.removeItem('user_permissions');
        localStorage.removeItem('is_superadmin');
      }
    });

    return () => unsubscribe();
  }, [loadPermissions]);

  // Memoized permission check functions
  const value = useMemo<PermissionContextValue>(() => ({
    permissions,
    isSuperAdmin,
    isLoading,
    error,

    hasPermission: (module: PermissionModule, action: PermissionAction) =>
      hasPermission(permissions ?? undefined, module, action, isSuperAdmin),

    canView: (module: PermissionModule) =>
      canView(permissions ?? undefined, module, isSuperAdmin),

    canEdit: (module: PermissionModule) =>
      canEdit(permissions ?? undefined, module, isSuperAdmin),

    canManage: (module: PermissionModule) =>
      canManage(permissions ?? undefined, module, isSuperAdmin),

    isViewer: (module: PermissionModule) =>
      isViewer(permissions ?? undefined, module, isSuperAdmin),

    isEditor: (module: PermissionModule) =>
      isEditor(permissions ?? undefined, module, isSuperAdmin),

    isManager: (module: PermissionModule) =>
      isManager(permissions ?? undefined, module, isSuperAdmin),

    isAdmin: (module: PermissionModule) =>
      isAdmin(permissions ?? undefined, module, isSuperAdmin), // Deprecated: Use isManager instead

    getModuleLevel: (module: PermissionModule) =>
      getModulePermission(permissions ?? undefined, module).level,

    getAccessibleModules: () =>
      getAccessibleModules(permissions ?? undefined, isSuperAdmin),

    refreshPermissions: loadPermissions,
  }), [permissions, isSuperAdmin, isLoading, error, loadPermissions]);

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

/**
 * Hook to access permission context
 */
export function usePermissions(): PermissionContextValue {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
}

/**
 * Hook to check a specific permission
 */
export function useHasPermission(
  module: PermissionModule,
  action: PermissionAction
): { hasPermission: boolean; isLoading: boolean } {
  const { hasPermission: checkPermission, isLoading } = usePermissions();
  const result = checkPermission(module, action);
  return { hasPermission: result.hasPermission, isLoading };
}

/**
 * Hook to check if user can edit a module
 */
export function useCanEdit(module: PermissionModule): { canEdit: boolean; isLoading: boolean } {
  const { canEdit: checkCanEdit, isLoading } = usePermissions();
  return { canEdit: checkCanEdit(module), isLoading };
}

/**
 * Hook to check if user can view a module
 */
export function useCanView(module: PermissionModule): { canView: boolean; isLoading: boolean } {
  const { canView: checkCanView, isLoading } = usePermissions();
  return { canView: checkCanView(module), isLoading };
}

/**
 * Hook to check if user can manage a module
 */
export function useCanManage(module: PermissionModule): { canManage: boolean; isLoading: boolean } {
  const { canManage: checkCanManage, isLoading } = usePermissions();
  return { canManage: checkCanManage(module), isLoading };
}

export default PermissionContext;

